import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  User, Bell, Users, CreditCard, ShieldCheck, Languages, HelpCircle, Trash2, Ban
} from "lucide-react";
import AppShell from "../components/app/AppShell";
import AccountSection from "../components/settings/AccountSection";
import NotificationsSection from "../components/settings/NotificationsSection";
import LanguagePreference from "../components/settings/LanguagePreference";
import AccountDeletionSection from "../components/settings/AccountDeletionSection";
import HelpSection from "../components/settings/HelpSection";
import BlockedConversationsSection from "../components/settings/BlockedConversationsSection";
import { Button } from "../components/ui/button";
import { useI18n } from "../i18n";

/**
 * RC-WEB-4 §21/§22 — Settings information architecture.
 * One section at a time instead of one endless vertical form. Desktop uses a
 * sub-navigation column; mobile stacks the same sections.
 */

const SECTIONS = [
  { id: "cuenta", key: "nav_account", icon: User },
  { id: "notificaciones", key: "nav_notifications", icon: Bell },
  { id: "contactos", key: "nav_trusted", icon: Users },
  { id: "membresia", key: "nav_membership", icon: CreditCard },
  { id: "privacidad", key: "nav_privacy", icon: ShieldCheck },
  { id: "bloqueos", key: "nav_blocks", icon: Ban },
  { id: "idioma", key: "nav_language", icon: Languages },
  { id: "ayuda", key: "nav_help", icon: HelpCircle },
  { id: "eliminar", key: "nav_delete", icon: Trash2 },
];

const Card = ({ title, description, children, testid }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid={testid}>
    <h2 className="mb-1 text-lg font-semibold text-[#0B2545]">{title}</h2>
    {description && <p className="mb-4 text-sm text-slate-600">{description}</p>}
    {children}
  </section>
);

const Settings = () => {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const initial = SECTIONS.some((s) => s.id === params.get("s"))
    ? params.get("s")
    : params.get("focus") === "push" ? "notificaciones" : "cuenta";
  const [active, setActive] = useState(initial);

  useEffect(() => {
    const next = new URLSearchParams(params);
    if (next.get("s") === active) return;
    next.set("s", active);
    // GATE 13 — cada sección es una entrada de historial: Ajustes → Idioma →
    // Atrás devuelve a la sección anterior, no fuera de Ajustes.
    setParams(next, { replace: !params.get("s") });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Browser Back/Forward must move between sections too.
  useEffect(() => {
    const fromUrl = params.get("s");
    if (fromUrl && SECTIONS.some((s) => s.id === fromUrl) && fromUrl !== active) {
      setActive(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const body = () => {
    switch (active) {
      case "notificaciones":
        return <NotificationsSection />;
      case "contactos":
        return (
          <Card
            title={t("app.set.trusted_title")}
            description={t("app.set.trusted_desc")}
            testid="settings-trusted-card"
          >
            <Link to="/contactos-de-confianza">
              <Button className="bg-[#1B84FF] text-white hover:bg-[#0F6FDB]" data-testid="settings-trusted-contacts-link">
                {t("app.set.trusted_btn")}
              </Button>
            </Link>
          </Card>
        );
      case "membresia":
        return (
          <Card
            title={t("app.set.membership_title")}
            description={t("app.set.membership_desc")}
            testid="settings-membership-card"
          >
            <Link to="/renovar">
              <Button className="bg-[#1B84FF] text-white hover:bg-[#0F6FDB]" data-testid="settings-membership-link">
                {t("app.set.membership_btn")}
              </Button>
            </Link>
          </Card>
        );
      case "privacidad":
        return (
          <Card
            title={t("app.set.privacy_title")}
            description={t("app.set.privacy_desc")}
            testid="settings-privacy-card"
          >
            <ul className="mb-4 space-y-2 text-sm text-slate-700">
              <li>• {t("app.set.privacy_b1")}</li>
              <li>• {t("app.set.privacy_b2")}</li>
              <li>• {t("app.set.privacy_b3")}</li>
              <li>• {t("app.set.privacy_b4")}</li>
            </ul>
            <div className="flex flex-wrap gap-2">
              <Link to="/privacidad">
                <Button variant="outline" className="border-slate-300 text-[#0B2545]" data-testid="settings-privacy-doc-link">
                  {t("app.set.privacy_doc")}
                </Button>
              </Link>
              <Link to="/terminos">
                <Button variant="outline" className="border-slate-300 text-[#0B2545]" data-testid="settings-terms-link">
                  {t("app.set.privacy_terms")}
                </Button>
              </Link>
            </div>
          </Card>
        );
      case "idioma":
        return <LanguagePreference />;
      case "bloqueos":
        return <BlockedConversationsSection />;
      case "ayuda":
        return <HelpSection />;
      case "eliminar":
        return <AccountDeletionSection />;
      default:
        return <AccountSection />;
    }
  };

  return (
    <AppShell title={t("app.settings.title")} subtitle={t("app.settings.subtitle")} maxWidth="max-w-5xl">
      <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
        <nav className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible" data-testid="settings-nav">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const isActive = active === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setActive(s.id)}
                data-testid={`settings-tab-${s.id}`}
                className={`flex shrink-0 items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#0B2545] text-white"
                    : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                }`}
              >
                <Icon className="h-4 w-4" /> {t(`app.set.${s.key}`)}
              </button>
            );
          })}
        </nav>
        <div data-testid={`settings-section-${active}`}>{body()}</div>
      </div>
    </AppShell>
  );
};

export default Settings;
